import PopupSection from 'Components/Layout/Popup';
import classNames from 'classnames';
import React from 'react';
import { IconChecked, IconReadyForCheck, IconSigned } from '../../../../Lib/Icons';
import styles from '../../../../Styles/Util/_variables.module.scss';
import './WorksheetStatus.scss';
import WorksheetStatusPopupContent from './WorksheetStatusPopupContent';
import { IApiStatusModel } from './types';

interface IWorksheetStatusProps {
  status: IApiStatusModel;
  /**
   * The background colour the status icon will be displayed on
   *
   * Note this defines the colour of the "donut hole" (which is rendered on top of a circular gradient).
   * We also adjust progress wheel colours to be suitable for this background.
   *
   * We're unlikely to need a wide range of colours here, so this isn't too sophisticated.
   */
  background?: 'white' | 'grey400';
  /**
   * Controls whether a popup appears when clicking on the status wheel.
   * Should be set to true when this status wheel appears within a navigation link.
   */
  disablePopup?: boolean;
}

const WorksheetStatus: React.FC<IWorksheetStatusProps> = ({ status, background = 'white', disablePopup = false }) => {
  let backgroundColour, unfilledForegroundColour;
  switch (background) {
    case 'grey400':
      backgroundColour = styles.colourGrey400;
      unfilledForegroundColour = styles.colourWhite;
      break;
    case 'white':
    default:
      backgroundColour = styles.colourWhite;
      unfilledForegroundColour = styles.colourGrey400;
      break;
  }

  const inProgressStyle = {
    background:
      'radial-gradient(' +
      backgroundColour +
      ' 20%, transparent 22%), conic-gradient(' +
      styles.colourGreen400 +
      ' 0% ' +
      (status.WorkProgress ?? 0) +
      '%, ' +
      unfilledForegroundColour +
      ' ' +
      (status.WorkProgress ?? 0) +
      '% 100%)',
  };

  const sharedClassNames = {
    'WorksheetStatus-withPopup': !disablePopup,
  };

  let StatusIcon = null;
  if (!!status.IsOnSignedReport) {
    StatusIcon = <IconSigned className={classNames('Icon-noTopMargin WorksheetStatus-Signed', sharedClassNames)} />;
  } else if (!!status.IsChecked) {
    StatusIcon = <IconChecked className={classNames('Icon-noTopMargin WorksheetStatus-Checked', sharedClassNames)} />;
  } else if (!!status.IsComplete) {
    StatusIcon = (
      <IconReadyForCheck className={classNames('Icon-noTopMargin WorksheetStatus-ReadyForCheck', sharedClassNames)} />
    );
  } else {
    StatusIcon = <div className={classNames('WorksheetStatus-InProgress', sharedClassNames)} style={inProgressStyle} />;
  }

  return (
    <PopupSection
      trigger={StatusIcon}
      content={<WorksheetStatusPopupContent status={status} />}
      disabled={disablePopup}
      alwaysEnabled={!disablePopup}
    />
  );
};
export { WorksheetStatus };
export default WorksheetStatus;
